export type CorporateEventChartItem = {
  date: string;
  eventType: string;
  label: string | null;
  splitMultiplier: number | null;
  subscriptionPercentage: number | null;
};

const PERCENT_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalizeToken = (value: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

export type CorporateEventKind =
  | "split"
  | "reverse_split"
  | "bonus"
  | "subscription"
  | "other";

export const getCorporateEventKind = (
  event: Pick<CorporateEventChartItem, "eventType" | "label">,
): CorporateEventKind => {
  const label = normalizeToken(event.label);
  const eventType = normalizeToken(event.eventType);

  if (label.includes("GRUPAMENTO")) return "reverse_split";
  if (label.includes("DESDOBRAMENTO")) return "split";
  if (label.includes("BONIFICACAO") || label.includes("BONIFICAÇÃO"))
    return "bonus";
  if (label.includes("SUBSCRICAO") || label.includes("SUBSCRIÇÃO"))
    return "subscription";
  if (eventType === "SUBSCRIPTIONS") return "subscription";
  if (eventType === "STOCK_DIVIDENDS") return "split";
  return "other";
};

export const getCorporateEventTypeLabel = (
  event: Pick<CorporateEventChartItem, "eventType" | "label">,
) => {
  const kind = getCorporateEventKind(event);
  if (kind === "reverse_split") return "Grupamento";
  if (kind === "split") return "Desdobramento";
  if (kind === "bonus") return "Bonificação";
  if (kind === "subscription") return "Subscrição";
  return event.label?.trim() || "Evento corporativo";
};

export const getCorporateEventColor = (
  event: Pick<CorporateEventChartItem, "eventType" | "label">,
) => {
  const kind = getCorporateEventKind(event);
  if (kind === "split") return "hsl(var(--primary))";
  if (kind === "reverse_split") return "hsl(var(--destructive))";
  if (kind === "bonus") return "hsl(var(--foreground))";
  if (kind === "subscription") return "hsl(var(--secondary))";
  return "hsl(var(--muted-foreground))";
};

export const getCorporateEventMagnitudeLabel = (
  event: Pick<
    CorporateEventChartItem,
    "splitMultiplier" | "subscriptionPercentage"
  >,
) => {
  if (event.splitMultiplier !== null && event.splitMultiplier > 0) {
    return `Fator ${event.splitMultiplier.toLocaleString("pt-BR")}`;
  }
  if (
    event.subscriptionPercentage !== null &&
    event.subscriptionPercentage > 0
  ) {
    return `${PERCENT_FORMATTER.format(event.subscriptionPercentage)}%`;
  }
  return null;
};

export const getCorporateEventDisplayLabel = (
  event: CorporateEventChartItem,
) => {
  const type = getCorporateEventTypeLabel(event);
  const magnitude = getCorporateEventMagnitudeLabel(event);
  return magnitude ? `${type} (${magnitude})` : type;
};
